import { useState } from 'react'
import cx from 'classnames'

import { ProjectScoringConfig } from '../api/api'

type Props = {
  config: ProjectScoringConfig
}

const Tag = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded bg-gold-100 border border-gold-200 text-coolGray-700">
    {children}
  </span>
)

const Section = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <div>
    <div className="text-[11px] font-semibold text-coolGray-400 uppercase tracking-wide mb-1.5">
      {label}
    </div>
    {children}
  </div>
)

const ScoringConfigSummary = ({ config }: Props) => {
  const [isOpen, setIsOpen] = useState(false)

  const { isoLanguage, languageSettings, tones } = config

  const forbiddenWords = (
    languageSettings?.vocabulary?.forbidden_words ?? []
  ).filter((w): w is string => !!w)

  const wordReplacements = (
    languageSettings?.vocabulary?.word_replacements ?? []
  )
    .filter((r): r is NonNullable<typeof r> => !!r)
    .map((r) => ({
      phrase: r.phrase ?? '',
      replacements: (r.replacements ?? []).filter((x): x is string => !!x),
    }))
    .filter((r) => r.phrase)

  const emojiFrequency = languageSettings?.emoji_frequency
  const emojiAllowed =
    !!emojiFrequency &&
    ((emojiFrequency.left_emojis ?? 0) > 0 ||
      (emojiFrequency.right_emojis ?? 0) > 0)

  const hasAnyConfig =
    isoLanguage ||
    tones.length > 0 ||
    forbiddenWords.length > 0 ||
    wordReplacements.length > 0

  if (!hasAnyConfig) {
    return null
  }

  return (
    <div className="border border-gold-200 rounded-lg overflow-hidden bg-white">
      <button
        type="button"
        className={cx(
          'flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-gold-50 transition-colors',
          isOpen && 'border-b border-gold-200'
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-sm font-semibold flex-1">
          Brand voice config used for scoring
        </span>
        <span className="text-[11px] text-coolGray-400">
          {tones.length} tone{tones.length === 1 ? '' : 's'}
          {isoLanguage ? ` · ${isoLanguage}` : ''}
        </span>
        <svg
          className={cx(
            'w-4 h-4 text-gray-400 transition-transform',
            isOpen && 'rotate-180'
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="p-4 flex flex-col gap-4">
          {isoLanguage && (
            <Section label="Language">
              <Tag>{isoLanguage}</Tag>
            </Section>
          )}

          {tones.length > 0 && (
            <Section label="Tones of voice">
              <div className="flex flex-col gap-2">
                {tones.map((tone, i) => (
                  <div
                    key={i}
                    className="border border-gold-200 rounded-md px-3 py-2"
                  >
                    <div className="text-sm font-medium text-coolGray-800">
                      {tone.name}
                    </div>
                    {tone.description && (
                      <div className="text-xs text-coolGray-500 mt-0.5">
                        {tone.description}
                      </div>
                    )}
                    {tone.instruction && (
                      <div className="text-xs text-coolGray-400 mt-1 italic">
                        {tone.instruction}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {forbiddenWords.length > 0 && (
            <Section label="Banned words">
              <div className="flex flex-wrap gap-1.5">
                {forbiddenWords.map((word, i) => (
                  <Tag key={i}>{word}</Tag>
                ))}
              </div>
            </Section>
          )}

          {wordReplacements.length > 0 && (
            <Section label="Preferred alternatives">
              <div className="flex flex-col gap-1">
                {wordReplacements.map((replacement, i) => (
                  <div key={i} className="text-xs text-coolGray-600">
                    <span className="font-medium">{replacement.phrase}</span>
                    {replacement.replacements.length > 0 && (
                      <> → {replacement.replacements.join(', ')}</>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          <Section label="Emoji">
            <Tag>{emojiAllowed ? 'Allowed' : 'Not allowed'}</Tag>
          </Section>
        </div>
      )}
    </div>
  )
}

export default ScoringConfigSummary
